import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Loader2, Zap, Command, Paperclip, X, Mic, Repeat, ChevronDown, Upload } from 'lucide-react';
import VoiceInput from './VoiceInput';
import RichTextEditor from './RichTextEditor';
import { Switch } from './ui/switch';

export default function QuickAddTask({ variant = 'floating', label = 'Quick Create', buttonClassName = '', controlledOpen, onControlledClose, initialValues, preloadedProjects, preloadedUsers }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (v) => { if (!v && onControlledClose) onControlledClose(); } : setInternalOpen;
  const { user: currentUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrence, setRecurrence] = useState({
    type: 'weekly', day_of_week: 0, day_of_month: 1,
  });
  const defaultForm = {
    title: '', description: '', project_id: '', assignee_id: '',
    priority: 'medium', task_type: 'task', status: 'backlog',
  };
  const [form, setForm] = useState(defaultForm);

  // Seed from preloaded data (avoids race with API fetch)
  useEffect(() => {
    if (preloadedProjects?.length) setProjects(prev => prev.length ? prev : preloadedProjects);
  }, [preloadedProjects]);
  useEffect(() => {
    if (preloadedUsers?.length) setUsers(prev => prev.length ? prev : preloadedUsers);
  }, [preloadedUsers]);

  // Apply initialValues when controlled dialog opens
  useEffect(() => {
    if (isControlled && controlledOpen && initialValues) {
      setForm({
        ...defaultForm,
        project_id: initialValues.project_id || '',
        assignee_id: initialValues.assignee_id || '',
      });
      setAttachments([]);
      setIsRecurring(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledOpen]);

  // Load projects + users on first open
  const loadData = useCallback(async () => {
    try {
      const [projRes, membersRes] = await Promise.all([
        api.get('/api/projects'),
        api.get('/api/orgs/members?exclude_clients=true').catch(() => ({ data: { members: [] } })),
      ]);
      setProjects(projRes.data.projects || []);
      const members = (membersRes.data.members || []).map(m => ({ ...m, id: m.user_id || m.id }));
      setUsers(members);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (open && projects.length === 0) loadData();
  }, [open, projects.length, loadData]);

  // Default assignee to logged-in user once users are loaded
  useEffect(() => {
    if (currentUser?.id && users.length > 0 && !form.assignee_id) {
      const match = users.find(u => u.id === currentUser.id);
      if (match) {
        setForm(f => ({ ...f, assignee_id: currentUser.id }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, users]);

  // Enrich assignee list with project members when project changes
  useEffect(() => {
    if (!form.project_id) return;
    api.get(`/api/v1/rbac/projects/${form.project_id}/members`)
      .then((res) => {
        const projMembers = (res.data.members || []).map(m => ({ ...m, id: m.user_id || m.id }));
        setUsers(prev => {
          const ids = new Set(prev.map(u => u.id));
          const merged = [...prev];
          for (const pm of projMembers) {
            if (!ids.has(pm.id)) {
              merged.push(pm);
              ids.add(pm.id);
            }
          }
          return merged;
        });
      })
      .catch(() => {});
  }, [form.project_id]);

  // Keyboard shortcut: Ctrl/Cmd + Shift + T
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'T') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.project_id) { toast.error('Select a project'); return; }
    setSaving(true);
    // Base64 encode description to bypass WAF blocking HTML content
    const rawDesc = (form.description || '').trim();
    const encodedDesc = rawDesc ? btoa(unescape(encodeURIComponent(rawDesc))) : '';
    try {
      if (isRecurring) {
        // Create a recurring task definition
        const payload = {
          title: form.title.trim(),
          description_b64: encodedDesc,
          project_id: form.project_id,
          priority: form.priority,
          task_type: form.task_type,
          status: form.status,
          recurrence: {
            type: recurrence.type,
            ...(recurrence.type === 'weekly' && { day_of_week: recurrence.day_of_week }),
            ...(recurrence.type === 'monthly' && { day_of_month: recurrence.day_of_month }),
          },
        };
        if (form.assignee_id) payload.assignee_id = form.assignee_id;
        await api.post('/api/recurring-tasks', payload);
        toast.success('Recurring task created! Tasks will be generated automatically.');
      } else {
        // Create a regular one-off task
        const payload = {
          title: form.title.trim(),
          description_b64: encodedDesc,
          project_id: form.project_id,
          priority: form.priority,
          task_type: form.task_type,
          status: form.status,
        };
        if (form.assignee_id) payload.assignee_id = form.assignee_id;
        const createRes = await api.post('/api/tasks', payload);
        const taskId = createRes.data?.id || createRes.data?._id;

        if (attachments.length > 0 && taskId) {
          setUploading(true);
          const uploadResults = await Promise.allSettled(
            attachments.map((file) => {
              const formData = new FormData();
              formData.append('file', file);
              return api.post(`/api/tasks/${taskId}/attachments`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
            })
          );
          const failed = uploadResults.filter((r) => r.status === 'rejected').length;
          const succeeded = attachments.length - failed;
          if (succeeded > 0) {
            toast.success(`Task created with ${succeeded} attachment${succeeded === 1 ? '' : 's'}.`);
          } else {
            toast.success('Task created');
          }
          if (failed > 0) {
            toast.error(`${failed} attachment${failed === 1 ? '' : 's'} failed to upload.`);
          }
        } else {
          toast.success('Task created');
        }
      }

      setOpen(false);
      setAttachments([]);
      setIsRecurring(false);
      setRecurrence({ type: 'weekly', day_of_week: 0, day_of_month: 1 });
      setForm({ title: '', description: '', project_id: form.project_id, assignee_id: currentUser?.id || '', priority: 'medium', task_type: 'task', status: 'backlog' });

      // Signal pages to refresh their data without a full page reload
      window.dispatchEvent(new CustomEvent('task-created'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create task');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  // Submit on Cmd+Enter
  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const ALLOWED_EXTENSIONS = new Set(['.png', '.jpg', '.jpeg', '.gif', '.webp', '.webm', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.zip', '.txt', '.md', '.pptx', '.svg']);

  const handleFilesSelected = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const accepted = [];
    const rejected = [];

    files.forEach((file) => {
      const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
      if (!ALLOWED_EXTENSIONS.has(ext)) {
        rejected.push(`${file.name} (unsupported)`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        rejected.push(`${file.name} (over 10MB)`);
        return;
      }
      accepted.push(file);
    });

    if (rejected.length > 0) {
      toast.error(`Some files were skipped: ${rejected.join(', ')}`);
    }
    if (accepted.length > 0) {
      setAttachments((prev) => [...prev, ...accepted]);
    }

    event.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      {/* Trigger — hidden in controlled mode */}
      {!isControlled && (variant === 'floating' ? (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-[#0073EA] hover:bg-[#0060B9] text-white rounded-full shadow-lg shadow-[#0073EA]/25 flex items-center justify-center hover:scale-105 active:scale-95 transition-all group"
          title="Quick Add Task (⌘⇧T)"
          data-testid="quick-add-fab"
        >
          <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-200" />
        </button>
      ) : variant === 'icon' ? (
        <button
          onClick={() => setOpen(true)}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-[#676879] dark:text-[#9CA0AF] hover:bg-[#F6F7FB] dark:hover:bg-[#2a2b30]"
          title="Quick Create (⌘⇧T)"
          data-testid="quick-create-icon-btn"
          aria-label="Quick Create"
        >
          <Plus className="w-4.5 h-4.5" />
        </button>
      ) : (
        <Button
          type="button"
          size="sm"
          onClick={() => setOpen(true)}
          className={`h-9 px-3 bg-[#0073EA] hover:bg-[#0060B9] text-white ${buttonClassName}`}
          data-testid="quick-create-button"
          title="Quick Create (⌘⇧T)"
        >
          <Plus className="w-4 h-4" />
          <span className="whitespace-nowrap">{label}</span>
        </Button>
      ))}

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6" onKeyDown={handleKeyDown} data-testid="quick-add-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#323338] dark:text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#0073EA]" />
              Quick Add Task
              <span className="ml-auto mr-6 text-[10px] text-[#9CA0AF] font-normal flex items-center gap-1">
                <Command className="w-3 h-3" />⇧T
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 mt-1">
            {/* Title — auto-focused */}
            <div className="flex items-center gap-2">
              <Input
                placeholder="What needs to be done?"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="text-base h-11 font-medium flex-1"
                autoFocus
                data-testid="quick-add-title"
              />
              <VoiceInput
                onTranscript={(text) => setForm(f => ({ ...f, title: f.title ? f.title + ' ' + text : text }))}
              />
            </div>

            {/* Project + Assignee row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-[#9CA0AF] uppercase font-semibold">Project *</Label>
                <Select value={form.project_id || 'none'} onValueChange={(v) => setForm({ ...form, project_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="h-9 text-sm" data-testid="quick-add-project">
                    <SelectValue placeholder="Select Project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>Select Project</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-[#9CA0AF] uppercase font-semibold">Assignee</Label>
                <Select value={form.assignee_id || 'unassigned'} onValueChange={(v) => setForm({ ...form, assignee_id: v === 'unassigned' ? '' : v })}>
                  <SelectTrigger className="h-9 text-sm" data-testid="quick-add-assignee">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {[...users].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Priority + Type + Status row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] text-[#9CA0AF] uppercase font-semibold">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">🔴 Urgent</SelectItem>
                    <SelectItem value="high">🟠 High</SelectItem>
                    <SelectItem value="medium">🔵 Medium</SelectItem>
                    <SelectItem value="low">⚪ Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-[#9CA0AF] uppercase font-semibold">Type</Label>
                <Select value={form.task_type} onValueChange={(v) => setForm({ ...form, task_type: v })}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="epic">Epic</SelectItem>
                    <SelectItem value="story">Story</SelectItem>
                    <SelectItem value="task">Task</SelectItem>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="subtask">Subtask</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-[#9CA0AF] uppercase font-semibold">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="backlog">Backlog</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="review">Review</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label className="text-[10px] text-[#9CA0AF] uppercase font-semibold">Description</Label>
              <div className="flex items-start gap-2">
                <div className="flex-1 max-h-[200px] overflow-y-auto rounded border border-transparent">
                  <RichTextEditor
                    placeholder="Add details (optional, type @ to mention)"
                    value={form.description}
                    onChange={(val) => setForm({ ...form, description: val })}
                    minHeight={60}
                    teamUsers={users}
                    data-testid="quick-add-description"
                  />
                </div>
                <VoiceInput
                  className="mt-1"
                  onTranscript={(text) => setForm(f => ({ ...f, description: f.description ? f.description + ' ' + text : text }))}
                />
              </div>
            </div>

            {/* Recurrence */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] text-[#9CA0AF] uppercase font-semibold flex items-center gap-1.5">
                  <Repeat className="w-3 h-3" />
                  Recurring Task
                </Label>
                <Switch
                  checked={isRecurring}
                  onCheckedChange={setIsRecurring}
                  data-testid="recurring-toggle"
                />
              </div>
              {isRecurring && (
                <div className="bg-[#F6F7FB] dark:bg-[#2a2b30] rounded-lg p-3 space-y-2.5 border border-[#E6E9EF] dark:border-[#3a3b40]">
                  <div>
                    <Label className="text-[10px] text-[#9CA0AF] uppercase font-semibold">Frequency</Label>
                    <Select value={recurrence.type} onValueChange={(v) => setRecurrence({ ...recurrence, type: v })}>
                      <SelectTrigger className="h-9 text-sm" data-testid="recurrence-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Every day</SelectItem>
                        <SelectItem value="daily_weekdays">Every weekday (Mon–Fri)</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {recurrence.type === 'weekly' && (
                    <div>
                      <Label className="text-[10px] text-[#9CA0AF] uppercase font-semibold">Day of Week</Label>
                      <Select value={String(recurrence.day_of_week)} onValueChange={(v) => setRecurrence({ ...recurrence, day_of_week: parseInt(v) })}>
                        <SelectTrigger className="h-9 text-sm" data-testid="recurrence-day-of-week">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d, i) => (
                            <SelectItem key={d} value={String(i)}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {recurrence.type === 'monthly' && (
                    <div>
                      <Label className="text-[10px] text-[#9CA0AF] uppercase font-semibold">Day of Month</Label>
                      <Select value={String(recurrence.day_of_month)} onValueChange={(v) => setRecurrence({ ...recurrence, day_of_month: parseInt(v) })}>
                        <SelectTrigger className="h-9 text-sm" data-testid="recurrence-day-of-month">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                            <SelectItem key={d} value={String(d)}>{d}{d === 1 || d === 21 ? 'st' : d === 2 || d === 22 ? 'nd' : d === 3 || d === 23 ? 'rd' : 'th'}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <p className="text-[10px] text-[#9CA0AF]">
                    Tasks will be created automatically on schedule.
                  </p>
                </div>
              )}
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <Label className="text-[10px] text-[#9CA0AF] uppercase font-semibold flex items-center gap-1.5">
                <Paperclip className="w-3 h-3" /> Attachments
              </Label>
              <div
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.dataset.dragCount = (parseInt(e.currentTarget.dataset.dragCount || '0') + 1); if (e.dataTransfer?.types?.includes('Files')) e.currentTarget.classList.add('!border-[#0073EA]', '!bg-[#0073EA]/5'); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); const count = parseInt(e.currentTarget.dataset.dragCount || '1') - 1; e.currentTarget.dataset.dragCount = count; if (count <= 0) { e.currentTarget.classList.remove('!border-[#0073EA]', '!bg-[#0073EA]/5'); e.currentTarget.dataset.dragCount = '0'; } }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('!border-[#0073EA]', '!bg-[#0073EA]/5'); e.currentTarget.dataset.dragCount = '0'; const files = Array.from(e.dataTransfer?.files || []); if (files.length) { const accepted = []; files.forEach(f => { const ext = `.${f.name.split('.').pop()?.toLowerCase()}`; if (ALLOWED_EXTENSIONS.has(ext) && f.size <= MAX_FILE_SIZE) accepted.push(f); }); if (accepted.length) setAttachments(prev => [...prev, ...accepted]); } }}
                onClick={() => document.getElementById('quick-add-file-input')?.click()}
                className="border-2 border-dashed border-[#E6E9EF] dark:border-[#3a3b40] rounded-lg p-3 text-center cursor-pointer hover:border-[#0073EA]/40 hover:bg-[#0073EA]/[0.02] transition-colors"
                data-testid="quick-add-dropzone"
              >
                <Upload className="w-4 h-4 text-[#9CA0AF] mx-auto mb-1" />
                <p className="text-xs text-[#9CA0AF]">Drag & drop files or click to browse</p>
                <p className="text-[10px] text-[#9CA0AF]/60 mt-0.5">PNG, JPG, PDF, Doc, Excel, CSV, ZIP, WEBM up to 10MB</p>
              </div>
              <input
                id="quick-add-file-input"
                type="file"
                multiple
                onChange={handleFilesSelected}
                accept=".png,.jpg,.jpeg,.gif,.webp,.webm,.pdf,.doc,.docx,.xls,.xlsx,.csv,.zip,.txt,.md,.pptx,.svg"
                className="hidden"
                data-testid="quick-add-attachments"
              />
              {attachments.length > 0 && (
                <div className="space-y-1">
                  {attachments.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between text-xs text-[#323338] dark:text-white bg-[#F6F7FB] dark:bg-[#2a2b30] px-2.5 py-2 rounded-md"
                    >
                      <span className="truncate">
                        {file.name} <span className="text-[#9CA0AF]">({Math.round(file.size / 1024)} KB)</span>
                      </span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeAttachment(index); }}
                        className="text-[#9CA0AF] hover:text-[#E2445C]"
                        aria-label={`Remove ${file.name}`}
                        data-testid={`quick-add-remove-file-${index}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between pt-1 gap-2">
              <span className="text-[10px] text-[#9CA0AF] flex items-center gap-1 justify-center sm:justify-start">
                <Command className="w-3 h-3" />Enter to save
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setOpen(false)} className="h-9 px-4 flex-1 sm:flex-none">Cancel</Button>
                <Button size="sm" onClick={handleSubmit} disabled={saving || uploading} className="h-9 px-5 bg-[#0073EA] hover:bg-[#0060B9] text-white flex-1 sm:flex-none" data-testid="quick-add-submit">
                  {saving || uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : isRecurring ? <Repeat className="w-3.5 h-3.5 mr-1.5" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                  {uploading ? 'Uploading...' : isRecurring ? 'Create Recurring' : 'Create Task'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
